import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PortalApiError, portalApi } from '../api';
import { getPortalAppName, getPortalLegalUrls } from '../config';
import { PHONE_PATTERN, SMS_CODE_MAX_LENGTH, SMS_COOLDOWN_SECONDS } from '../constants';
import { markDeletionCancelled, saveSession } from '../authStorage';
import logger from '../logger';
import { PortalMessages as M } from '../messages';
import { buildBanbanOauthUrl, clearSkipAutoSso, isEmbeddedInIframe, markSkipAutoSso, parseBanbanOauthCallback, stripBanbanOauthQuery } from '../ssoRedirect';
function mapSsoError(code) {
    switch (code) {
        case 'SSO_NOT_CONFIGURED':
            return M.SSO_ERR_NOT_CONFIGURED;
        case 'SSO_DENIED':
        case 'access_denied':
            return M.SSO_ERR_DENIED;
        case 'SSO_STATE':
            return M.SSO_ERR_STATE;
        case 'SSO_TOKEN':
            return M.SSO_ERR_TOKEN;
        case 'SSO_PHONE':
            return M.SSO_ERR_PHONE;
        case 'SSO_TICKET':
            return M.SSO_ERR_TICKET;
        case 'USER_DISABLED':
            return M.SSO_ERR_DISABLED;
        case 'USER_DELETED':
            return M.SSO_ERR_DELETED;
        default:
            return M.SSO_ERR_GENERIC;
    }
}
/**
 * 门户登录 / 注册页（响应式）。
 */
export default function LoginPage({ onLoggedIn }) {
    const [agree, setAgree] = useState(false);
    const [code, setCode] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [error, setError] = useState('');
    const [formMode, setFormMode] = useState('login');
    const [loading, setLoading] = useState(false);
    const [loginMode, setLoginMode] = useState('password');
    const [password, setPassword] = useState('');
    const [phone, setPhone] = useState('');
    const [smsCooldown, setSmsCooldown] = useState(0);
    const [ssoEnabled, setSsoEnabled] = useState(false);
    const [ssoClientId, setSsoClientId] = useState('');
    const [ssoRedirectUri, setSsoRedirectUri] = useState('');
    const [ssoLoginPageUrl, setSsoLoginPageUrl] = useState('');
    const [ssoScanPageUrl, setSsoScanPageUrl] = useState('');
    const [scanIframeUrl, setScanIframeUrl] = useState('');
    const ssoCompletingRef = useRef(false);
    useEffect(() => {
        let cancelled = false;
        portalApi.ssoStatus().then(status => {
            if (!cancelled) {
                setSsoEnabled(Boolean(status?.enabled));
                setSsoClientId(status?.clientId || '');
                setSsoRedirectUri(status?.redirectUri || '');
                setSsoLoginPageUrl(status?.loginPageUrl || '');
                setSsoScanPageUrl(status?.scanPageUrl || '');
            }
        }).catch(e => {
            logger.warn(`查询 SSO 开关失败 | ${e?.message || e}`);
        });
        return () => {
            cancelled = true;
        };
    }, []);
    useEffect(() => {
        const oauth = parseBanbanOauthCallback();
        if (oauth.error && !oauth.accessToken) {
            markSkipAutoSso();
            setError(mapSsoError(oauth.error));
            stripBanbanOauthQuery();
            logger.warn(`办办 SSO 回调失败 | ssoError=${oauth.error}`);
            return;
        }
        if (!oauth.accessToken || !oauth.code || ssoCompletingRef.current) {
            return;
        }
        ssoCompletingRef.current = true;
        setLoading(true);
        portalApi.loginBanbanOauth(oauth.code, oauth.accessToken).then(result => {
            saveSession(result.accessToken, result.user);
            if (result.deletionCancelled) {
                markDeletionCancelled();
            }
            stripBanbanOauthQuery();
            clearSkipAutoSso();
            logger.info(`办办 SSO 登录成功 | userId=${result.user.id}`
                + ` deletionCancelled=${Boolean(result.deletionCancelled)}`);
            onLoggedIn();
        }).catch(e => {
            const message = e instanceof PortalApiError
                ? e.message
                : (e?.message || M.SSO_ERR_TICKET);
            setError(message);
            stripBanbanOauthQuery();
            markSkipAutoSso();
            logger.warn(`办办 OAuth 兑换失败 | ${message}`);
        }).finally(() => {
            setLoading(false);
            ssoCompletingRef.current = false;
        });
    }, [onLoggedIn]);
    useEffect(() => {
        if (smsCooldown <= 0) {
            return;
        }
        const timer = window.setInterval(() => {
            setSmsCooldown(prev => (prev <= 1 ? 0 : prev - 1));
        }, 1000);
        return () => window.clearInterval(timer);
    }, [smsCooldown > 0]);
    const showPassword = formMode === 'register' || loginMode === 'password';
    const showSms = formMode === 'register' || loginMode === 'sms';
    const legal = getPortalLegalUrls();
    const onSendSms = useCallback(async () => {
        setError('');
        if (!PHONE_PATTERN.test(phone)) {
            setError(M.INVALID_PHONE);
            return;
        }
        try {
            await portalApi.sendSms(phone, formMode === 'register' ? 'REGISTER' : 'LOGIN');
            setSmsCooldown(SMS_COOLDOWN_SECONDS);
            logger.info(`已发送短信验证码 | phone=${phone.slice(0, 3)}**** purpose=${formMode}`);
        }
        catch (e) {
            setError(e?.message || M.SEND_SMS_FAILED);
        }
    }, [phone, formMode]);
    const onSubmit = useCallback(async (event) => {
        event.preventDefault();
        setError('');
        if (!agree) {
            setError(M.NEED_AGREE);
            return;
        }
        if (!PHONE_PATTERN.test(phone)) {
            setError(M.INVALID_PHONE);
            return;
        }
        setLoading(true);
        try {
            let result;
            if (formMode === 'register') {
                result = await portalApi.register({
                    phone,
                    code,
                    password,
                    displayName: displayName || undefined
                });
            }
            else if (loginMode === 'password') {
                result = await portalApi.loginPassword(phone, password);
            }
            else {
                result = await portalApi.loginSms(phone, code);
            }
            saveSession(result.accessToken, result.user);
            if (result.deletionCancelled) {
                markDeletionCancelled();
            }
            logger.info(`登录成功 | userId=${result.user.id} deletionCancelled=${Boolean(result.deletionCancelled)}`);
            onLoggedIn();
        }
        catch (e) {
            const message = e instanceof PortalApiError
                ? e.message
                : (e?.message || M.LOGIN_FAILED);
            setError(message);
            logger.warn(`登录失败 | ${message}`);
        }
        finally {
            setLoading(false);
        }
    }, [agree, phone, formMode, loginMode, code, password, displayName, onLoggedIn]);
    const onSsoClick = useCallback(() => {
        setError('');
        if (!agree) {
            setError(M.NEED_AGREE);
            return;
        }
        if (!ssoClientId || !ssoRedirectUri) {
            setError(M.SSO_ERR_NOT_CONFIGURED);
            return;
        }
        const page = isEmbeddedInIframe()
            ? (ssoLoginPageUrl || 'https://oa.nxbanban.com/#/SSOLogin')
            : (ssoScanPageUrl || 'https://oa.nxbanban.com/#/scanCodeLogin');
        const target = buildBanbanOauthUrl(page, ssoClientId, ssoRedirectUri);
        logger.info(`用户点击办办登录 | embedded=${isEmbeddedInIframe()}`);
        if (isEmbeddedInIframe()) {
            window.location.assign(target);
        }
        else {
            setScanIframeUrl(target);
        }
    }, [agree, ssoEnabled, ssoClientId, ssoRedirectUri, ssoLoginPageUrl, ssoScanPageUrl]);
    return (React.createElement("div", { className: 'portal-login-shell' },
        React.createElement("div", { className: 'portal-login-card' },
            React.createElement("div", { className: 'portal-brand' },
                React.createElement("div", { className: 'portal-brand-mark' }, "M"),
                React.createElement("h1", null, getPortalAppName()),
                React.createElement("p", null, M.APP_SUBTITLE)),
            scanIframeUrl ? (React.createElement("div", { className: 'portal-sso-scan' },
                React.createElement("p", { className: 'portal-sso-scan-hint' }, M.SSO_SCAN_HINT),
                React.createElement("iframe", { className: 'portal-sso-scan-frame', src: scanIframeUrl, title: M.SSO_LOGIN }),
                React.createElement("button", { className: 'portal-switch-mode', onClick: () => {
                        markSkipAutoSso();
                        setScanIframeUrl('');
                    }, type: 'button' }, M.SSO_BACK_ACCOUNT))) : (React.createElement(React.Fragment, null,
                formMode === 'login' && (React.createElement("div", { className: 'portal-tab-row' },
                    React.createElement("button", { className: `portal-tab ${loginMode === 'password' ? 'active' : ''}`, onClick: () => setLoginMode('password'), type: 'button' }, M.PASSWORD_LOGIN),
                    React.createElement("button", { className: `portal-tab ${loginMode === 'sms' ? 'active' : ''}`, onClick: () => setLoginMode('sms'), type: 'button' }, M.SMS_LOGIN))),
                React.createElement("form", { onSubmit: onSubmit },
                    React.createElement("label", { className: 'portal-field' },
                        M.PHONE,
                        React.createElement("input", { autoComplete: 'tel', maxLength: 11, onChange: e => setPhone(e.target.value), placeholder: M.PHONE_PLACEHOLDER, value: phone })),
                    formMode === 'register' && (React.createElement("label", { className: 'portal-field' },
                        M.DISPLAY_NAME,
                        React.createElement("input", { onChange: e => setDisplayName(e.target.value), placeholder: M.DISPLAY_NAME_PLACEHOLDER, value: displayName }))),
                    showPassword && (React.createElement("label", { className: 'portal-field' },
                        M.PASSWORD,
                        React.createElement("input", { autoComplete: formMode === 'register' ? 'new-password' : 'current-password', onChange: e => setPassword(e.target.value), placeholder: M.PASSWORD_PLACEHOLDER, type: 'password', value: password }))),
                    showSms && (React.createElement("label", { className: 'portal-field' },
                        M.SMS_CODE,
                        React.createElement("div", { className: 'portal-sms-row' },
                            React.createElement("input", { maxLength: SMS_CODE_MAX_LENGTH, onChange: e => setCode(e.target.value), placeholder: M.SMS_CODE_PLACEHOLDER, value: code }),
                            React.createElement("button", { disabled: smsCooldown > 0 || loading, onClick: onSendSms, type: 'button' }, smsCooldown > 0
                                ? `${smsCooldown}${M.SMS_COOLDOWN_SUFFIX}`
                                : M.SEND_SMS)),
                        React.createElement("div", { className: 'portal-sms-tip' }, M.SMS_NOT_CONNECTED_TIP))),
                    error ? React.createElement("div", { className: 'portal-error' }, error) : null,
                    React.createElement("button", { className: 'portal-primary-btn', disabled: loading || !agree, type: 'submit' }, loading
                        ? M.SUBMITTING
                        : formMode === 'register' ? M.REGISTER : M.LOGIN)),
                React.createElement("label", { className: 'portal-agreement' },
                    React.createElement("input", { checked: agree, onChange: e => setAgree(e.target.checked), type: 'checkbox' }),
                    React.createElement("span", null,
                        M.AGREE_PREFIX,
                        React.createElement("a", { href: legal.termsUrl, rel: 'noreferrer', target: '_blank' }, M.TERMS),
                        M.AND,
                        React.createElement("a", { href: legal.privacyUrl, rel: 'noreferrer', target: '_blank' }, M.PRIVACY))),
                React.createElement("button", { className: 'portal-switch-mode', onClick: () => {
                        setFormMode(formMode === 'login' ? 'register' : 'login');
                        setCode('');
                        setPassword('');
                        setError('');
                    }, type: 'button' }, formMode === 'login' ? M.GO_REGISTER : M.GO_LOGIN),
                React.createElement("div", { className: 'portal-coming-soon' },
                    M.OTHER_METHODS,
                    React.createElement("span", null, M.WECHAT_SOON),
                    ssoEnabled ? (React.createElement("button", { className: 'portal-sso-btn', disabled: loading, onClick: onSsoClick, type: 'button' }, loading ? M.SSO_REDIRECTING : M.SSO_LOGIN)) : null))))));
}
